import { useTranslation } from "react-i18next";
import React, { useMemo, useState, useRef, useEffect } from "react";
import { useLocation } from "wouter";
import { Layout } from "@/components/layout";
import { QuoteEditor } from "@/features/quotes/QuoteEditor";
import { useQuotes } from "@/hooks/use-quotes";
import { useTemplates } from "@/hooks/use-templates";
import useTemplateFromUrl from "@/hooks/use-template-from-url";
import { useInitialProducts } from "@/hooks/use-products";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { QuoteFormData } from "@/features/quotes/QuoteEditor";
import { getCustomers } from "@/requests/customers/get-customers";
import { useQuery } from "@tanstack/react-query";
import { useProductEvents } from "@/hooks/use-product-events";

export default function NewQuote() {
  const { t } = useTranslation();
  const [, setLocation] = useLocation();
  const { templateId, clearTemplateParam } = useTemplateFromUrl();
  const { toast } = useToast();

  // Save as template state
  const [saveTemplateDialogOpen, setSaveTemplateDialogOpen] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const templateDataRef = useRef<any>(null);

  // Fetch initial products for ProductCatalog
  const {
    products: initialProducts,
    isLoading: initialProductsLoading,
    error: initialProductsError,
  } = useInitialProducts();

  // Fetch customers for customer selector
  const { data: customers } = useQuery(getCustomers());

  // Fetch templates for template selector
  const {
    templates,
    createTemplate,
    isCreating: isCreatingTemplate,
  } = useTemplates();

  // Create quote mutation
  const { createQuote, isCreating } = useQuotes();
  const { capture } = useProductEvents({ component: "NewQuote" });

  // Load template data if templateId exists
  const selectedTemplate = useMemo(() => {
    if (!templateId || !templates) return null;
    return templates.find((t) => t.uid === templateId);
  }, [templateId, templates]);

  // Fire once when a template is loaded from the URL param.
  useEffect(() => {
    if (!selectedTemplate) return;
    capture({
      event: "quote_template_applied",
      context: {
        templateId: selectedTemplate.uid,
        productCount: (selectedTemplate.products as any[])?.length ?? 0,
      },
    });
    // Only fire on first load of this template.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate?.uid]);

  // Convert template to initial quote data
  const initialData = useMemo(() => {
    if (!selectedTemplate) return undefined;

    return {
      templateId: selectedTemplate.uid,
      title: "", // Templates don't have title, leave blank for user to fill
      description: selectedTemplate.description || "",
      laborHours: selectedTemplate.laborHours || 0,
      laborRate: selectedTemplate.laborRate || 0,
      markupPercent: selectedTemplate.markupPercent || 0,
      partsAffectPrice: selectedTemplate.partsAffectPrice ?? true,
      discountPercentage: selectedTemplate.discountPercentage || 0,
      discountDescription: selectedTemplate.discountDescription || "",
      notes: selectedTemplate.defaultNotes || "",
      products: (selectedTemplate.products as any) || [],
      quoteServices: selectedTemplate.quoteServices || [],
      quoteCustomParts: selectedTemplate.quoteCustomParts || [],
    };
  }, [selectedTemplate]);

  const handleSave = async (data: QuoteFormData) => {
    try {
      await createQuote(data);

      // Clear template param if it exists
      if (templateId) {
        clearTemplateParam();
      }

      // Navigate back to quotes list on success
      setLocation("/quotes");
    } catch (error) {
      // Error is already handled by useQuotes toast
      console.error("Failed to create quote:", error);
    }
  };

  const handleCancel = () => {
    // Clear template param if it exists
    if (templateId) {
      clearTemplateParam();
    }
    // Navigate back to quotes list on cancel
    setLocation("/quotes");
  };

  // Handler for "Save as Template" button click
  const handleSaveAsTemplate = (currentFormData: QuoteFormData) => {
    // Validate required fields for template saving
    if (!currentFormData.description?.trim()) {
      toast({
        title: t("pages.newQuote.descriptionRequired"),
        description:
          t("pages.newQuote.pleaseAddADescription"),
        variant: "destructive",
      });
      return;
    }

    // Store the form data (converting quote-specific fields to template format)
    templateDataRef.current = {
      description: currentFormData.description,
      laborHours: currentFormData.laborHours,
      laborRate: currentFormData.laborRate,
      partsCostPremarkup: currentFormData.partsCostPremarkup,
      partsCostPostmarkup: currentFormData.partsCostPostmarkup,
      markupPercent: currentFormData.markupPercent,
      partsAffectPrice: currentFormData.partsAffectPrice,
      discountPercentage: currentFormData.discountPercentage,
      discountDescription: currentFormData.discountDescription,
      defaultNotes: currentFormData.notes, // Save as defaultNotes in the template
      quoteServices: currentFormData.quoteServices,
      products: currentFormData.products,
      quoteCustomParts: currentFormData.quoteCustomParts,
      // Required fields for template creation
      amountPrediscount: currentFormData.amountPrediscount,
      amountPresalesTax: currentFormData.amountPresalesTax,
    };

    // Open the dialog
    setSaveTemplateDialogOpen(true);
  };

  // Handler for saving the template with name
  const handleConfirmSaveTemplate = async () => {
    if (templateName.trim() === "") {
      toast({
        title: t("pages.newQuote.templateNameIsRequired"),
        variant: "destructive",
      });
      return;
    }

    if (templateDataRef.current) {
      try {
        await createTemplate({
          ...templateDataRef.current,
          name: templateName,
        });

        // Close dialog and reset state
        setSaveTemplateDialogOpen(false);
        setTemplateName("");
        templateDataRef.current = null;

        toast({
          title: t("pages.newQuote.templateSavedSuccessfully"),
          description: `Template "${templateName}" has been created.`,
        });
      } catch (error) {
        // Error handling is already done in the useTemplates hook
        console.error("Error saving template:", error);
      }
    }
  };

  // Handler for canceling template save
  const handleCancelSaveTemplate = () => {
    setSaveTemplateDialogOpen(false);
    setTemplateName("");
    templateDataRef.current = null;
  };

  return (
    <>
      <Layout headerContent={
        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">{t("pages.newQuote.quotes")}</span>
          <span className="text-muted-foreground">/</span>
          <span className="font-semibold">{t("pages.newQuote.newQuote")}</span>
        </div>
      }>
        <QuoteEditor
          mode="create"
          entityType="quote"
          initialData={initialData as any}
          onSave={handleSave as any}
          onCancel={handleCancel}
          onSaveAsTemplate={handleSaveAsTemplate}
          isSubmitting={isCreating}
          customers={customers as any}
          templates={templates as any}
          products={initialProducts}
          productsLoading={initialProductsLoading}
          productsError={initialProductsError}
        />
      </Layout>

      {/* Save as Template Dialog */}
      <Dialog
        open={saveTemplateDialogOpen}
        onOpenChange={setSaveTemplateDialogOpen}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{t("pages.newQuote.saveAsTemplate")}</DialogTitle>
            <DialogDescription>
              {t("pages.newQuote.saveThisQuoteAs")}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="template-name" className="text-right col-span-1">
                {t("pages.newQuote.name")}
              </Label>
              <Input
                id="template-name"
                placeholder={t("pages.newQuote.enterTemplateName")}
                className="col-span-3"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleConfirmSaveTemplate();
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancelSaveTemplate}>
              {t("common.cancel")}
            </Button>
            <Button
              onClick={handleConfirmSaveTemplate}
              disabled={isCreatingTemplate || !templateName.trim()}
            >
              {isCreatingTemplate ? t("common.saving") : t("pages.newQuote.saveTemplate")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
